import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import fleetApi, { fleetError } from "@/lib/fleetApi";
import { boardTrips } from "@/lib/tripsFetch";
import { toast } from "sonner";
import { Panel, PageHeader, Loading } from "@/components/portal/widgets";
import { TimeEditRow } from "@/components/portal/TimeEditRow";
import { Info, RefreshCw } from "lucide-react";
import { getOrgTimeZone } from "@/lib/time";

const NEEDS_TIME = /shift|time|late|window|early/i;

const localParts = (iso) => {
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-US", { timeZone: getOrgTimeZone(), weekday: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
    .formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
  return { wd: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].indexOf(p.weekday), hm: `${p.hour}:${p.minute}` };
};

// Fallback for roles without AI-plan access: unassigned trips whose pickup is outside every driver's shift.
const offShiftTrips = async () => {
  const [trips, drivers] = await Promise.all([boardTrips().then((data) => ({ data })), fleetApi.get("/drivers")]);
  const shifts = (drivers.data || []).filter((d) => d.status !== "inactive").map((d) => d.shift_template || {});
  return (trips.data || []).filter((t) => t.scheduled_pickup && !t.assigned_driver_id && !t.assigned_driver_name && !/complete|cancel|no.?show/i.test(t.status || ""))
    .filter((t) => {
      const { wd, hm } = localParts(t.scheduled_pickup);
      return !shifts.some((s) => s[String(wd)] && s[String(wd)].start <= hm && hm < s[String(wd)].end);
    })
    .map((t) => ({ trip_id: t.id, reason: "No drivers on shift at pickup time", trip: t }));
};

export default function TimeFixesPage() {
  const [params] = useSearchParams();
  const focus = params.get("trip");
  const [rows, setRows] = useState(null);
  const [openId, setOpenId] = useState(focus);
  const [saved, setSaved] = useState({});

  const load = useCallback(async () => {
    setRows(null);
    try {
      const [plan, first] = await Promise.all([
        fleetApi.post("/trips/assign-plan", {}).then((r) => r.data).catch(async () => ({ unassignable: await offShiftTrips().catch(() => []) })),
        focus ? fleetApi.get(`/trips/${focus}`).then((r) => r.data).catch(() => null) : null,
      ]);
      const flagged = (plan.unassignable || []).filter((u) => NEEDS_TIME.test(u.reason || "") && u.trip_id !== focus);
      const trips = await Promise.all(flagged.map((u) => (u.trip ? Promise.resolve({ t: u.trip, why: u.reason })
        : fleetApi.get(`/trips/${u.trip_id}`).then((r) => ({ t: r.data, why: u.reason })).catch(() => null))));
      const list = trips.filter(Boolean).sort((a, b) => String(a.t.scheduled_pickup).localeCompare(String(b.t.scheduled_pickup)));
      setRows(first ? [{ t: first, why: (plan.unassignable || []).find((u) => u.trip_id === focus)?.reason, focus: true }, ...list] : list);
    } catch (e) { toast.error(fleetError(e, "Couldn't load trips.")); setRows([]); }
  }, [focus]);

  useEffect(() => { load(); }, [load]);

  const onSaved = (id, time) => {
    setSaved((s) => ({ ...s, [id]: time }));
    const next = rows.find((r) => r.t.id !== id && !saved[r.t.id]);
    setOpenId(next?.t.id || null);
  };

  if (!rows) return <Loading />;
  const left = rows.filter((r) => !saved[r.t.id]).length;

  return (
    <div data-testid="time-fixes-page">
      <PageHeader title="Edit Trip Times" subtitle={`${left} trip${left === 1 ? "" : "s"} to fix · each change affects that trip only`}>
        <button onClick={load} data-testid="time-fixes-refresh" className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-medium text-slate-200 hover:bg-slate-800">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </PageHeader>
      <div className="mb-4 flex items-start gap-2 rounded-lg border border-blue-500/30 bg-blue-500/10 px-4 py-3 text-xs text-blue-200" data-testid="time-fixes-info">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        Listed: the trip you picked, plus every unassigned trip the AI can't plan because of its time (for example, pickup before any driver's shift). Save one and the next opens automatically. Standing orders, return legs and other trips stay the same.
      </div>
      <Panel testid="time-fixes-list">
        {rows.length === 0 ? (
          <p className="px-5 py-12 text-center text-sm text-slate-400" data-testid="time-fixes-empty">No trips need a time change right now.</p>
        ) : (
          <ul className="divide-y divide-slate-800">
            {rows.map(({ t, why }) => (
              <TimeEditRow key={t.id} t={t} why={why} open={openId === t.id} onOpen={setOpenId} onSaved={onSaved} saved={saved[t.id]} />
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}
